import React, { useState, useEffect } from 'react';
import { 
  User, 
  Phone, 
  Mail, 
  MapPin, 
  Calendar, 
  Users,
  IdCard,
  Globe,
  GraduationCap,
  BookOpen,
  Camera,
  CheckCircle,
  UserCircle,
  Home,
  Building,
  Flag,
  Star,
  School,
  Heart,
  Award,
  ChevronDown,
  Filter
} from 'lucide-react';
import { formatDate, formatPhoneNumber } from '../../utils/format';
import api from '../api';

const EtudiantInfoTab = ({ etudiant, etudiantId }) => {
  const [parcoursData, setParcoursData] = useState(null);
  const [anneeSelectionnee, setAnneeSelectionnee] = useState('');
  const [loadingParcours, setLoadingParcours] = useState(false);
  const [anneesAcademiques, setAnneesAcademiques] = useState([]);

  useEffect(() => {
    fetchParcoursAcademique();
  }, [etudiantId]);

  const fetchParcoursAcademique = async (anneeId = null) => {
    setLoadingParcours(true);
    try {
      let url = `/etudiant/parcours-academique/${etudiantId}/`;
      if (anneeId) {
        url += `?annee=${anneeId}`;
      }
      const response = await api.get(url);
      setParcoursData(response.data.parcours);
      setAnneesAcademiques(response.data.annees_academiques);
    } catch (error) {
      console.error('Erreur lors du chargement du parcours:', error);
    } finally {
      setLoadingParcours(false);
    }
  };

  const handleAnneeChange = (anneeId) => {
    setAnneeSelectionnee(anneeId);
    fetchParcoursAcademique(anneeId);
  };

  const InfoCard = ({ title, children, icon: Icon, gradient = false, className = "" }) => (
    <div className={`relative overflow-hidden rounded-xl shadow-lg transition-all duration-300 hover:shadow-xl transform hover:-translate-y-1 ${
      gradient 
        ? 'bg-gradient-to-br from-[#202654] to-[#1a1f45] text-white' 
        : 'bg-white border border-gray-100'
    } ${className}`}>
      {gradient && (
        <div className="absolute inset-0 bg-white opacity-5"></div>
      )}
      <div className="relative p-6">
        <div className="flex items-center mb-6">
          <div className={`p-3 rounded-xl mr-4 ${
            gradient 
              ? 'bg-white/10 backdrop-blur-sm' 
              : 'bg-gradient-to-br from-[#202654] to-[#1a1f45]'
          }`}>
            <Icon className={`h-6 w-6 ${gradient ? 'text-white' : 'text-white'}`} />
          </div>
          <div>
            <h3 className={`text-xl font-bold ${gradient ? 'text-white' : 'text-gray-900'}`}>
              {title}
            </h3>
            {gradient && (
              <p className="text-white/80 text-sm mt-1">Profil étudiant</p>
            )}
          </div>
        </div>
        {children}
      </div>
    </div>
  );

  const InfoItem = ({ label, value, icon: Icon, highlight = false }) => (
    <div className={`flex items-center justify-between py-4 px-4 rounded-lg transition-colors duration-200 ${
      highlight ? 'bg-blue-50 border border-blue-100' : 'hover:bg-gray-50'
    }`}>
      <div className="flex items-center">
        {Icon && (
          <Icon className={`h-4 w-4 mr-3 ${highlight ? 'text-blue-600' : 'text-gray-400'}`} />
        )}
        <span className={`font-medium ${highlight ? 'text-blue-900' : 'text-gray-700'}`}>
          {label}
        </span>
      </div>
      <span className={`font-semibold ${highlight ? 'text-blue-800' : 'text-gray-900'}`}>
        {value || <span className="text-gray-400 italic">Non renseigné</span>}
      </span>
    </div>
  );

  const StatusBadge = ({ status, label, color = 'green' }) => {
    const colorClasses = {
      green: 'bg-green-100 text-green-800 border-green-200',
      blue: 'bg-blue-100 text-blue-800 border-blue-200',
      purple: 'bg-purple-100 text-purple-800 border-purple-200',
      indigo: 'bg-indigo-100 text-indigo-800 border-indigo-200',
      yellow: 'bg-yellow-100 text-yellow-800 border-yellow-200',
      red: 'bg-red-100 text-red-800 border-red-200'
    };

    const iconColorClasses = {
      green: 'text-green-600',
      blue: 'text-blue-600',
      purple: 'text-purple-600',
      indigo: 'text-indigo-600',
      yellow: 'text-yellow-600',
      red: 'text-red-600'
    };

    return (
      <div className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium border ${colorClasses[color] || colorClasses.green}`}>
        <CheckCircle className={`h-4 w-4 mr-2 ${iconColorClasses[color] || iconColorClasses.green}`} />
        {label}
      </div>
    );
  };

  return (
    <div className="space-y-4">
      {/* En-tête avec actions */}
      <div className="bg-gradient-to-r from-gray-50 to-blue-50 rounded-lg p-4 border border-gray-100">
        <div className="flex justify-between items-center">
          <div>
            <h2 className="text-xl font-bold text-gray-900 mb-1">Profil Étudiant</h2>
            <p className="text-sm text-gray-600">Informations complètes et détaillées</p>
          </div>
        </div>
      </div>

      {/* Carte de profil principal avec photo */}
      <InfoCard title={`${etudiant.nom} ${etudiant.prenom}`} icon={Star} gradient={true}>
        <div className="flex items-center space-x-6">
          <div className="relative">
            {etudiant.photo ? (
              <img 
                src={etudiant.photo} 
                alt={`${etudiant.nom} ${etudiant.prenom}`}
                className="w-24 h-24 object-cover rounded-xl border-4 border-white/20 shadow-lg"
              />
                         ) : (
               <div className="w-24 h-24 bg-white/10 rounded-xl border-4 border-white/20 flex items-center justify-center group cursor-pointer hover:bg-white/20 transition-colors relative">
                 <UserCircle className="h-16 w-16 text-white/60 group-hover:text-white/80 transition-colors" />
                 <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                   <Camera className="h-6 w-6 text-white" />
                 </div>
               </div>
             )}
            <div className="absolute -bottom-2 -right-2 bg-green-400 rounded-full p-2">
              <CheckCircle className="h-4 w-4 text-white" />
            </div>
          </div>
          <div className="flex-1">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-white/80 text-sm">Matricule</p>
                <p className="text-white font-bold text-lg">{etudiant.matricule || 'Non assigné'}</p>
              </div>
                             <div>
                 <p className="text-white/80 text-sm">Statut</p>
                 <div className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-green-400/20 text-green-100 border border-green-400/30">
                   <CheckCircle className="h-4 w-4 mr-2 text-green-400" />
                   Étudiant actif
                 </div>
               </div>
            </div>
            <div className="grid grid-cols-2 gap-4 mt-4">
              <div>
                <p className="text-white/80 text-sm">Formation</p>
                <p className="text-white font-semibold">
                  {etudiant.get_filiere && etudiant.get_niveau ? 
                    `${etudiant.get_filiere} - ${etudiant.get_niveau}` : 
                    'Non inscrit'}
                </p>
              </div>
              <div>
                <p className="text-white/80 text-sm">Classe</p>
                <p className="text-white font-semibold">{etudiant.get_classe || 'Non assigné'}</p>
              </div>
            </div>
          </div>
        </div>
      </InfoCard>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Informations personnelles */}
        <InfoCard title="Informations Personnelles" icon={User}>
          <div className="space-y-2">
            <InfoItem 
              label="Nom" 
              value={etudiant.nom} 
              icon={User}
              highlight={true}
            />
            <InfoItem 
              label="Prénom" 
              value={etudiant.prenom} 
              icon={User}
              highlight={true}
            />
            <InfoItem 
              label="Genre" 
              value={etudiant.genre} 
              icon={UserCircle}
            />
            <InfoItem 
              label="Date de naissance" 
              value={formatDate(etudiant.date_naissance)} 
              icon={Calendar}
            />
            <InfoItem 
              label="Lieu de naissance" 
              value={etudiant.lieu_naissance} 
              icon={MapPin}
            />
            <InfoItem 
              label="Nationalité" 
              value={etudiant.nationalite} 
              icon={Flag}
            />
          </div>
        </InfoCard>

        {/* Informations de contact */}
        <InfoCard title="Contact & Adresse" icon={Phone}>
          <div className="space-y-2">
            <InfoItem 
              label="Email" 
              value={etudiant.email} 
              icon={Mail}
              highlight={true}
            />
            <InfoItem 
              label="Téléphone" 
              value={formatPhoneNumber(etudiant.contact)} 
              icon={Phone}
              highlight={true}
            />
            <InfoItem 
              label="Adresse" 
              value={etudiant.adresse} 
              icon={Home}
            />
          </div>
        </InfoCard>

        {/* Section supprimée - fusionnée avec la section Parcours Académique avec filtre */}

        {/* Informations du parent/tuteur */}
        <InfoCard title="Parent/Tuteur" icon={Heart}>
          <div className="space-y-2">
            <InfoItem 
              label="Nom du parent" 
              value={etudiant.parent?.nom_parent} 
              icon={User}
            />
            <InfoItem 
              label="Prénom du parent" 
              value={etudiant.parent?.prenom_parent} 
              icon={User}
            />
            <InfoItem 
              label="Contact parent" 
              value={formatPhoneNumber(etudiant.parent?.contact_parent)} 
              icon={Phone}
              highlight={true}
            />
            <InfoItem 
              label="Email parent" 
              value={etudiant.parent?.email_parent} 
              icon={Mail}
            />
            <InfoItem 
              label="Adresse parent" 
              value={etudiant.parent?.adresse_parent} 
              icon={Home}
            />
          </div>
        </InfoCard>
        
        {/* Parcours Académique */}
        <InfoCard title="Parcours Académique" icon={GraduationCap}>
          <div className="space-y-4">
            {/* Informations de base */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <InfoItem 
                label="Matricule" 
                value={etudiant.matricule || 'Non assigné'} 
                icon={IdCard}
                highlight={true}
              />
              <InfoItem 
                label="Classe actuelle" 
                value={etudiant.get_classe || 'Non assigné'} 
                icon={Building}
                highlight={true}
              />
              <InfoItem 
                label="Filière" 
                value={etudiant.get_filiere || 'Non inscrit'} 
                icon={BookOpen}
              />
              <InfoItem 
                label="Niveau" 
                value={etudiant.get_niveau || 'Non inscrit'} 
                icon={School}
              />
            </div>

            {/* Séparateur */}
            <div className="border-t border-gray-200 pt-4">
              <h4 className="text-base font-semibold text-gray-900 mb-3">Historique des années</h4>
              
              {/* Filtre par année académique */}
              <div className="flex items-center space-x-4 mb-4">
                <div className="flex items-center">
                  <Filter className="h-5 w-5 text-gray-500 mr-2" />
                  <span className="text-sm font-medium text-gray-700">Filtrer par année:</span>
                </div>
                <div className="relative">
                  <select
                    value={anneeSelectionnee}
                    onChange={(e) => handleAnneeChange(e.target.value)}
                    className="appearance-none bg-white border border-gray-300 rounded-lg px-4 py-2 pr-8 text-sm focus:outline-none focus:ring-2 focus:ring-[#202654] focus:border-transparent"
                  >
                                      <option value="">Toutes les années</option>
                  {anneesAcademiques.map((annee) => (
                    <option key={annee.id} value={annee.id}>
                      {annee.libelle}
                    </option>
                  ))}
                  </select>
                  <ChevronDown className="absolute right-2 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
                </div>
              </div>

              {/* Contenu du parcours */}
              {loadingParcours ? (
                <div className="flex items-center justify-center py-4">
                  <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-[#202654]"></div>
                  <span className="ml-2 text-sm text-gray-600">Chargement du parcours...</span>
                </div>
              ) : parcoursData && parcoursData.length > 0 ? (
                <div className="space-y-2">
                  {parcoursData.map((parcours, index) => (
                    <div key={parcours.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg border border-gray-200">
                      <div className="flex items-center space-x-4">
                        <div className="w-10 h-10 bg-[#202654] text-white rounded-full flex items-center justify-center font-bold text-sm">
                          {index + 1}
                        </div>
                        <div>
                          <h4 className="font-semibold text-gray-900">
                            {parcours.classe.libelle}
                          </h4>
                          <p className="text-sm text-gray-600">
                            {parcours.classe.filiere.libelle} - {parcours.classe.niveau.libelle}
                          </p>
                        </div>
                      </div>
                                          <div className="text-right">
                      <div className="font-medium text-[#202654]">
                        {parcours.annee.libelle}
                      </div>
                      <div className="text-sm text-gray-500">
                        {formatDate(parcours.annee.date_debut)} - {formatDate(parcours.annee.date_fin)}
                      </div>
                    </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-4 text-gray-500">
                  <GraduationCap className="h-8 w-8 mx-auto mb-2 text-gray-300" />
                  <p className="text-sm">Aucun parcours académique trouvé</p>
                  {anneeSelectionnee && (
                    <p className="text-xs mt-1">pour l'année sélectionnée</p>
                  )}
                </div>
              )}
            </div>
          </div>
        </InfoCard>
      </div>

      {/* Badges de statut et achievements */}
      <div className="bg-white rounded-lg shadow-lg border border-gray-100 p-4">
        <div className="flex items-center mb-4">
          <div className="p-2 rounded-lg mr-3 bg-gradient-to-br from-yellow-400 to-orange-500">
            <Award className="h-5 w-5 text-white" />
          </div>
          <h3 className="text-lg font-bold text-gray-900">Statut & Badges</h3>
        </div>
        <div className="flex flex-wrap gap-2">
          <StatusBadge status="active" label="Étudiant Actif" color="green" />
          <StatusBadge status="enrolled" label="Inscrit" color="blue" />
          {etudiant.photo && <StatusBadge status="verified" label="Profil Complet" color="purple" />}
          {etudiant.email && <StatusBadge status="contact" label="Email Vérifié" color="indigo" />}
        </div>
      </div>
    </div>
  );
};

export default EtudiantInfoTab; 